import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from 'hooks/useAppStore';
import { hasPermission, hasAnyRole } from 'shared/permissions/permissionMatrix';
import type { Permission } from 'types';
import type { Role } from 'constants/roles';
import { ROUTES } from 'constants/routes';

interface PermissionGuardProps {
  children: React.ReactNode;
  permission?: Permission;
  roles?: Role[];
  fallback?: React.ReactNode;
}

export function PermissionGuard({
  children,
  permission,
  roles,
  fallback,
}: PermissionGuardProps) {
  const userRoles = useAppSelector((state) => state.auth.user?.roles ?? []);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  if (permission && !hasPermission(userRoles, permission)) {
    return fallback ? <>{fallback}</> : <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  if (roles && !hasAnyRole(userRoles, roles)) {
    return fallback ? <>{fallback}</> : <Navigate to={ROUTES.DASHBOARD} replace />;
  }

  return <>{children}</>;
}

interface CanProps {
  permission: Permission;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function Can({ permission, children, fallback = null }: CanProps) {
  const userRoles = useAppSelector((state) => state.auth.user?.roles ?? []);

  if (!hasPermission(userRoles, permission)) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
